import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Layers, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { todayISO } from "../lib/format";
import SymbolAutocomplete from "./SymbolAutocomplete";

export default function BulkTradeDialog({ clients, onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingLtp, setFetchingLtp] = useState(false);
  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [txType, setTxType] = useState("BUY");
  const [price, setPrice] = useState("");
  const [txDate, setTxDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  // map: clientId -> { selected, quantity }
  const [allocs, setAllocs] = useState({});

  useEffect(() => {
    if (open) {
      // reset selections
      const init = {};
      for (const c of clients) init[c.id] = { selected: false, quantity: "" };
      setAllocs(init);
      setSymbol("");
      setName("");
      setPrice("");
      setTxType("BUY");
      setTxDate(todayISO());
      setNotes("");
    }
  }, [open, clients]);

  const selectedIds = useMemo(
    () => Object.keys(allocs).filter((id) => allocs[id]?.selected),
    [allocs]
  );

  const toggle = (id) =>
    setAllocs((s) => ({ ...s, [id]: { ...s[id], selected: !s[id]?.selected } }));

  const setQty = (id, v) =>
    setAllocs((s) => ({ ...s, [id]: { ...s[id], quantity: v } }));

  const selectAll = () => {
    setAllocs((s) => {
      const allSelected = Object.values(s).every((v) => v.selected);
      const next = {};
      for (const id of Object.keys(s)) next[id] = { ...s[id], selected: !allSelected };
      return next;
    });
  };

  const distributeEqually = () => {
    const totalRaw = prompt("Enter total quantity to distribute equally across selected clients:");
    if (totalRaw === null) return;
    const total = Number(totalRaw);
    if (!total || total <= 0 || selectedIds.length === 0) {
      toast.error("Enter a positive number and select at least one client");
      return;
    }
    const per = +(total / selectedIds.length).toFixed(2);
    setAllocs((s) => {
      const next = { ...s };
      for (const id of selectedIds) next[id] = { ...next[id], quantity: String(per) };
      return next;
    });
  };

  const fetchLtp = async () => {
    if (!symbol) {
      toast.error("Enter a symbol first");
      return;
    }
    setFetchingLtp(true);
    try {
      const { data } = await apiClient.get("/prices/quote", { params: { symbol } });
      setPrice(data.price.toFixed(2));
      toast.success(`Loaded LTP for ${data.symbol}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Quote not found");
    } finally {
      setFetchingLtp(false);
    }
  };

  const submit = async () => {
    if (!symbol || !price || !txDate) {
      toast.error("Fill symbol, price and date");
      return;
    }
    const allocations = selectedIds
      .map((id) => ({ client_id: id, quantity: Number(allocs[id]?.quantity || 0) }))
      .filter((a) => a.quantity > 0);
    if (allocations.length === 0) {
      toast.error("Select at least one client with a quantity > 0");
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/advisor/trades/bulk", {
        symbol,
        name: name || null,
        tx_type: txType,
        price: Number(price),
        tx_date: txDate,
        notes,
        allocations,
      });
      toast.success(`Recorded ${data.tx_type} ${data.symbol} for ${data.created} clients`);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record bulk trade");
    } finally {
      setLoading(false);
    }
  };

  const totalQty = selectedIds.reduce(
    (s, id) => s + (Number(allocs[id]?.quantity) || 0),
    0
  );
  const totalValue = totalQty * (Number(price) || 0);

  return (
    <>
      <button
        data-testid="bulk-trade-btn"
        onClick={() => setOpen(true)}
        className="border border-border hover:bg-secondary px-4 py-2.5 text-xs font-medium uppercase tracking-wider flex items-center gap-2"
      >
        <Layers className="h-4 w-4" strokeWidth={1.5} />
        Bulk Trade
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Layers className="h-5 w-5" strokeWidth={1.5} />
              Bulk trade — one script, many clients
            </DialogTitle>
          </DialogHeader>

          {/* Trade details */}
          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            {["BUY", "SELL"].map((t) => (
              <button
                key={t}
                data-testid={`bulk-tx-type-${t}`}
                onClick={() => setTxType(t)}
                className={`p-2.5 text-xs font-medium uppercase tracking-wider ${
                  txType === t
                    ? t === "BUY"
                      ? "bg-profit text-white"
                      : "bg-loss text-white"
                    : "bg-card text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-12 gap-3 mt-3">
            <div className="col-span-12 sm:col-span-6">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Symbol
              </label>
              <div className="mt-1">
                <SymbolAutocomplete
                  value={symbol}
                  onChange={setSymbol}
                  onSelect={(item) => {
                    setSymbol(item.symbol);
                    if (!name) setName(item.name);
                  }}
                  testId="bulk-symbol"
                />
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Price (₹)
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  data-testid="bulk-price"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="flex-1 border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring tabular"
                />
                <button
                  type="button"
                  data-testid="bulk-fetch-ltp"
                  onClick={fetchLtp}
                  disabled={fetchingLtp}
                  className="border border-border hover:bg-secondary px-2 text-xs uppercase tracking-wider flex items-center gap-1 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${fetchingLtp ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  LTP
                </button>
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Trade date
              </label>
              <input
                data-testid="bulk-date"
                type="date"
                value={txDate}
                onChange={(e) => setTxDate(e.target.value)}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {/* Client allocations */}
          <div className="mt-5 border border-subtle">
            <div className="flex items-center justify-between px-3 py-2 bg-secondary border-b border-subtle">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Clients ({selectedIds.length} selected)
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="bulk-distribute"
                  onClick={distributeEqually}
                  className="text-[11px] flex items-center gap-1 text-foreground hover:text-foreground"
                >
                  <Wand2 className="h-3 w-3" strokeWidth={1.5} />
                  Distribute equally
                </button>
                <button
                  type="button"
                  data-testid="bulk-select-all"
                  onClick={selectAll}
                  className="text-[11px] text-foreground hover:text-foreground underline"
                >
                  Toggle all
                </button>
              </div>
            </div>
            <div className="max-h-72 overflow-auto">
              {clients.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No clients yet. Create a client first.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-card border-b border-subtle sticky top-0">
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="w-10 p-2"></th>
                      <th className="text-left p-2 font-medium">Client</th>
                      <th className="text-right p-2 font-medium w-32">Quantity</th>
                      <th className="text-right p-2 font-medium w-32">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => {
                      const a = allocs[c.id] || { selected: false, quantity: "" };
                      const qn = Number(a.quantity) || 0;
                      const val = qn * (Number(price) || 0);
                      return (
                        <tr
                          key={c.id}
                          data-testid={`bulk-client-row-${c.id}`}
                          className={`border-b border-subtle ${a.selected ? "bg-secondary" : ""}`}
                        >
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              data-testid={`bulk-client-select-${c.id}`}
                              checked={!!a.selected}
                              onChange={() => toggle(c.id)}
                            />
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-[11px] text-muted-foreground">{c.email}</div>
                          </td>
                          <td className="p-2">
                            <input
                              data-testid={`bulk-client-qty-${c.id}`}
                              type="number"
                              value={a.quantity}
                              onChange={(e) => setQty(c.id, e.target.value)}
                              disabled={!a.selected}
                              placeholder="0"
                              className="w-full border border-border px-2 py-1 text-sm text-right bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring tabular disabled:bg-secondary disabled:text-muted-foreground/70"
                            />
                          </td>
                          <td className="p-2 text-right tabular text-muted-foreground">
                            {qn && price
                              ? `₹${val.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border-t border-border">
              <div className="bg-card p-3">
                <div className="text-[10px] text-muted-foreground tracking-widest uppercase">Selected</div>
                <div className="text-base font-semibold tabular mt-0.5">{selectedIds.length}</div>
              </div>
              <div className="bg-card p-3">
                <div className="text-[10px] text-muted-foreground tracking-widest uppercase">Total Qty</div>
                <div className="text-base font-semibold tabular mt-0.5">{totalQty}</div>
              </div>
              <div className="bg-card p-3">
                <div className="text-[10px] text-muted-foreground tracking-widest uppercase">Price</div>
                <div className="text-base font-semibold tabular mt-0.5">
                  {price ? `₹${Number(price).toLocaleString("en-IN")}` : "—"}
                </div>
              </div>
              <div className="bg-card p-3">
                <div className="text-[10px] text-muted-foreground tracking-widest uppercase">Total Value</div>
                <div className="text-base font-semibold tabular mt-0.5">
                  {totalValue
                    ? `₹${totalValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Notes (optional)
            </label>
            <input
              data-testid="bulk-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring"
            />
          </div>

          <DialogFooter className="mt-4">
            <button
              onClick={() => setOpen(false)}
              className="border border-border hover:bg-secondary px-4 py-2 text-xs font-medium uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              data-testid="bulk-submit"
              onClick={submit}
              disabled={loading}
              className={`text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md disabled:opacity-50 active:scale-[0.98] ${
                txType === "BUY" ? "bg-profit hover:brightness-110" : "bg-loss hover:brightness-110"
              }`}
            >
              {loading ? "Recording…" : `Record ${txType} for ${selectedIds.length} clients`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
